import { RowData } from '../App';
import type { LtrRtlControl } from '../classes/Block';
import { ColDef, ColGroupDef } from 'ag-grid-community';

export type WysiwygLayoutMode = 'horizontal' | 'vertical';

export interface WysiwygVisualCell {
  sourceIndex: number;
  visualColumnIndex: number;
  sourceField: string;
}

export interface WysiwygVisualRow {
  __wysiwygRowIndex: number;
  __sourceIndexes: Array<number | null>;
  __wysiwygSourceField?: string;
  __wysiwygRowLabel?: string;
  [field: string]: string | number | Array<number | null> | undefined;
}

export interface WysiwygGridModel {
  enabled: boolean;
  layoutMode: WysiwygLayoutMode;
  columnCount: number;
  sourceFields: string[];
  sourceColumnDefs: ColDef[];
  rowData: WysiwygVisualRow[];
  columnDefs: Array<ColDef | ColGroupDef>;
  allSourceFields: string[];
  reverseColumns: boolean;
}

const RTL_CHAR_PATTERN = /[\u0590-\u05ff\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff\ufb1d-\ufdff\ufe70-\ufeff]/;
const LTR_CHAR_PATTERN = /[A-Za-z]/;

export function isRtlText(value: unknown): boolean {
  const text: string = String(value ?? '');
  for (const char of text) {
    if (RTL_CHAR_PATTERN.test(char)) return true;
    if (LTR_CHAR_PATTERN.test(char)) return false;
  }
  return false;
}

export function shouldReverseVisualColumns(
  rowData: RowData[],
  textFields: string[],
  columnCount: number,
  ltrRtlControl: LtrRtlControl = 'auto'
): boolean {
  if (columnCount <= 1) return false;
  if (ltrRtlControl !== 'auto') return ltrRtlControl === 'rtl';
  const textValues: string[] = [];
  for (const row of rowData) {
    for (const field of textFields) {
      const value: unknown = row[field];
      const text: string = String(value ?? '').trim();
      if (text) textValues.push(text);
    }
  }
  return textValues.length > 0 && textValues.every(isRtlText);
}

export function getWysiwygCellField(visualColumnIndex: number, sourceField: string): string {
  return `wysiwyg_${visualColumnIndex}_${sourceField}`;
}

export function getColumnFields(columnDefs: ColDef[]): string[] {
  return columnDefs
    .map((colDef: ColDef) => (typeof colDef.field === 'string' ? colDef.field : ''))
    .filter((field: string) => field.length > 0);
}

export function getWysiwygSourceIndex(
  visualRowIndex: number,
  visualColumnIndex: number,
  columnCount: number,
  rowCount: number,
  reverseColumns: boolean
): number | null {
  const chunkStart: number = visualRowIndex * columnCount;
  const remainingInChunk: number = Math.max(0, Math.min(columnCount, rowCount - chunkStart));
  if (visualColumnIndex < 0 || visualColumnIndex >= remainingInChunk) return null;
  const chunkOffset: number = reverseColumns && remainingInChunk > 1
    ? remainingInChunk - 1 - visualColumnIndex
    : visualColumnIndex;
  return chunkStart + chunkOffset;
}

export function buildWysiwygRows(
  rowData: RowData[],
  sourceFields: string[],
  columnCount: number,
  reverseColumns: boolean
): WysiwygVisualRow[] {
  const safeColumnCount: number = Math.max(1, Math.floor(columnCount));
  const visualRows: WysiwygVisualRow[] = [];
  for (let visualRowIndex = 0; visualRowIndex * safeColumnCount < rowData.length; visualRowIndex += 1) {
    const visualRow: WysiwygVisualRow = {
      __wysiwygRowIndex: visualRowIndex,
      __sourceIndexes: [],
    };
    for (let visualColumnIndex = 0; visualColumnIndex < safeColumnCount; visualColumnIndex += 1) {
      const sourceIndex: number | null = getWysiwygSourceIndex(
        visualRowIndex,
        visualColumnIndex,
        safeColumnCount,
        rowData.length,
        reverseColumns
      );
      visualRow.__sourceIndexes[visualColumnIndex] = sourceIndex;
      for (const sourceField of sourceFields) {
        visualRow[getWysiwygCellField(visualColumnIndex, sourceField)] =
          sourceIndex === null ? '' : rowData[sourceIndex]?.[sourceField] ?? '';
      }
    }
    visualRows.push(visualRow);
  }
  return visualRows;
}

export function buildVerticalWysiwygRows(
  rowData: RowData[],
  sourceFields: string[],
  columnCount: number,
  reverseColumns: boolean
): WysiwygVisualRow[] {
  const safeColumnCount: number = Math.max(1, Math.floor(columnCount));
  const visualRows: WysiwygVisualRow[] = [];
  for (let chunkIndex = 0; chunkIndex * safeColumnCount < rowData.length; chunkIndex += 1) {
    for (const sourceField of sourceFields) {
      const visualRow: WysiwygVisualRow = {
        __wysiwygRowIndex: visualRows.length,
        __sourceIndexes: [],
        __wysiwygSourceField: sourceField,
        __wysiwygRowLabel: getVerticalRowLabel(sourceField, sourceFields),
      };
      for (let visualColumnIndex = 0; visualColumnIndex < safeColumnCount; visualColumnIndex += 1) {
        const sourceIndex: number | null = getWysiwygSourceIndex(
          chunkIndex,
          visualColumnIndex,
          safeColumnCount,
          rowData.length,
          reverseColumns
        );
        visualRow.__sourceIndexes[visualColumnIndex] = sourceIndex;
        visualRow[getWysiwygCellField(visualColumnIndex, sourceField)] =
          sourceIndex === null ? '' : rowData[sourceIndex]?.[sourceField] ?? '';
      }
      visualRows.push(visualRow);
    }
  }
  return visualRows;
}

export function getWysiwygVisualCell(
  cellField: string,
  sourceFields: string[]
): { visualColumnIndex: number; sourceField: string } | null {
  for (const sourceField of sourceFields) {
    const suffix: string = `_${sourceField}`;
    if (!cellField.startsWith('wysiwyg_') || !cellField.endsWith(suffix)) continue;
    const visualColumnRaw: string = cellField.slice('wysiwyg_'.length, -suffix.length);
    const visualColumnIndex: number = Number(visualColumnRaw);
    if (!Number.isInteger(visualColumnIndex) || visualColumnIndex < 0) return null;
    return { visualColumnIndex, sourceField };
  }
  return null;
}

export function getWysiwygVisualColumnIndex(cellField: string, sourceFields: string[]): number | null {
  const visualCell = getWysiwygVisualCell(cellField, sourceFields);
  if (visualCell) return visualCell.visualColumnIndex;
  const verticalColumnIndex: number = getVerticalVisualColumnIndex(cellField);
  return verticalColumnIndex >= 0 ? verticalColumnIndex : null;
}

export function getWysiwygSourceColumnDefs(
  columnDefs: ColDef[],
  isSingleColumn?: boolean,
  isSongBlock?: boolean,
  layoutMode: WysiwygLayoutMode = 'horizontal'
): ColDef[] {
  const fieldDefs: ColDef[] = columnDefs.filter((colDef: ColDef) => typeof colDef.field === 'string');
  if (layoutMode === 'vertical' && fieldDefs.length >= 2) {
    if (isSingleColumn && !isSongBlock) {
      const valueDef: ColDef = fieldDefs[1];
      return [{ ...valueDef, headerName: valueDef.headerName ?? 'Value', editable: true }];
    }
    return [
      { ...fieldDefs[0], headerName: fieldDefs[0].headerName ?? 'Label', editable: true },
      { ...fieldDefs[1], headerName: fieldDefs[1].headerName ?? 'Value', editable: true },
    ];
  }
  if (isSingleColumn && !isSongBlock && fieldDefs.length >= 2) {
    const valueDef: ColDef = fieldDefs[1];
    return [{ ...valueDef, headerName: valueDef.headerName ?? 'Value', editable: true }];
  }
  return fieldDefs;
}

function getVerticalRowLabel(sourceField: string, sourceFields: string[]): string {
  if (sourceFields.length === 1) return 'V';
  const sourceIndex: number = sourceFields.indexOf(sourceField);
  if (sourceIndex === 0) return 'L';
  if (sourceIndex === 1) return 'V';
  return sourceField;
}

export function buildWysiwygColumnDefs(sourceColumnDefs: ColDef[], columnCount: number): ColGroupDef[] {
  return Array.from({ length: columnCount }, (_, visualColumnIndex: number): ColGroupDef => ({
    headerName: `Column ${visualColumnIndex + 1}`,
    marryChildren: true,
    children: sourceColumnDefs.map((sourceColDef: ColDef, sourceFieldIndex: number): ColDef => {
      const sourceField: string = sourceColDef.field as string;
      const field: string = getWysiwygCellField(visualColumnIndex, sourceField);
      return {
        ...sourceColDef,
        field,
        colId: field,
        headerName: sourceColDef.headerName ?? sourceField,
        wrapText: true,
        autoHeight: true,
        rowDrag: visualColumnIndex === 0 && sourceFieldIndex === 0,
        editable: (params) => {
          const visualRow: WysiwygVisualRow | undefined = params.data as WysiwygVisualRow | undefined;
          return Boolean(visualRow && visualRow.__sourceIndexes[visualColumnIndex] !== null && sourceColDef.editable !== false);
        },
        cellClass: (params) => {
          const visualRow: WysiwygVisualRow | undefined = params.data as WysiwygVisualRow | undefined;
          const classNames: string[] = ['wysiwyg-value-cell'];
          if (visualRow?.__sourceIndexes[visualColumnIndex] === null) {
            classNames.push('ag-cell-not-editable', 'wysiwyg-empty-cell');
          }
          if (typeof sourceColDef.cellClass === 'string') {
            classNames.push(sourceColDef.cellClass);
          }
          return classNames;
        },
      };
    }),
  }));
}

export function buildVerticalWysiwygColumnDefs(sourceColumnDefs: ColDef[], columnCount: number): ColDef[] {
  const sourceFieldDefs: Map<string, ColDef> = new Map<string, ColDef>(
    sourceColumnDefs.map((sourceColDef: ColDef) => [sourceColDef.field as string, sourceColDef])
  );
  const rowLabelColumn: ColDef = {
    field: '__wysiwygRowLabel',
    headerName: '',
    editable: false,
    rowDrag: (params) => {
      const visualRow: WysiwygVisualRow | undefined = params.data as WysiwygVisualRow | undefined;
      return visualRow?.__wysiwygSourceField === sourceColumnDefs[0]?.field;
    },
    width: 44,
    minWidth: 40,
    maxWidth: 48,
    flex: 0,
    suppressSizeToFit: true,
    suppressAutoSize: true,
    lockPosition: 'left',
    suppressMovable: true,
    cellClass: 'wysiwyg-vertical-row-label ag-cell-not-editable',
    headerClass: 'ag-header-cell-not-editable',
  };
  const valueColumns: ColDef[] = Array.from({ length: columnCount }, (_, visualColumnIndex: number): ColDef => ({
    field: getWysiwygCellField(visualColumnIndex, sourceColumnDefs[0]?.field as string),
    colId: `wysiwyg_vertical_${visualColumnIndex}`,
    headerName: `Column ${visualColumnIndex + 1}`,
    // Share the space left after the fixed row-label column rather than shrink
    // to the contents of individual cells.
    flex: 1,
    minWidth: 48,
    suppressSizeToFit: true,
    wrapText: true,
    autoHeight: true,
    editable: (params) => {
      const visualRow: WysiwygVisualRow | undefined = params.data as WysiwygVisualRow | undefined;
      const sourceField: string | undefined = visualRow?.__wysiwygSourceField;
      const sourceColDef: ColDef | undefined = sourceField ? sourceFieldDefs.get(sourceField) : undefined;
      return Boolean(visualRow && sourceField && visualRow.__sourceIndexes[visualColumnIndex] !== null && sourceColDef?.editable !== false);
    },
    valueGetter: (params) => {
      const visualRow: WysiwygVisualRow | undefined = params.data as WysiwygVisualRow | undefined;
      const sourceField: string | undefined = visualRow?.__wysiwygSourceField;
      return sourceField ? visualRow?.[getWysiwygCellField(visualColumnIndex, sourceField)] ?? '' : '';
    },
    valueSetter: (params) => {
      const visualRow: WysiwygVisualRow | undefined = params.data as WysiwygVisualRow | undefined;
      const sourceField: string | undefined = visualRow?.__wysiwygSourceField;
      if (!visualRow || !sourceField) return false;
      visualRow[getWysiwygCellField(visualColumnIndex, sourceField)] = params.newValue ?? '';
      return true;
    },
    cellClass: (params) => {
      const visualRow: WysiwygVisualRow | undefined = params.data as WysiwygVisualRow | undefined;
      const classNames: string[] = ['wysiwyg-value-cell', 'wysiwyg-vertical-value-cell'];
      if (visualRow?.__sourceIndexes[visualColumnIndex] === null) {
        classNames.push('ag-cell-not-editable', 'wysiwyg-empty-cell');
      }
      return classNames;
    },
  }));
  return [rowLabelColumn, ...valueColumns];
}

export function buildWysiwygGridModel(args: {
  rowData: RowData[];
  columnDefs: ColDef[];
  numColumns?: number;
  isLogoBlock?: boolean;
  isSingleColumn?: boolean;
  isSongBlock?: boolean;
  labelValueAlignment?: 'horizontal' | 'vertical' | 'vertical_stacked';
  ltrRtlControl?: LtrRtlControl;
}): WysiwygGridModel {
  const columnCount: number = Math.max(1, Math.floor(Number(args.numColumns) || 1));
  const layoutMode: WysiwygLayoutMode = args.labelValueAlignment === 'vertical' || args.labelValueAlignment === 'vertical_stacked'
    ? 'vertical'
    : 'horizontal';
  const enabled: boolean = !args.isLogoBlock && (columnCount > 1 || (layoutMode === 'vertical' && !args.isSongBlock));
  const sourceColumnDefs: ColDef[] = enabled
    ? getWysiwygSourceColumnDefs(args.columnDefs, args.isSingleColumn, args.isSongBlock, layoutMode)
    : [];
  const sourceFields: string[] = getColumnFields(sourceColumnDefs);
  const reverseColumns: boolean = shouldReverseVisualColumns(
    args.rowData,
    sourceFields,
    columnCount,
    args.ltrRtlControl ?? 'auto'
  );
  const modelEnabled: boolean = enabled && sourceFields.length > 0;
  return {
    enabled: modelEnabled,
    layoutMode,
    columnCount,
    sourceFields,
    sourceColumnDefs,
    rowData: modelEnabled
      ? layoutMode === 'vertical'
        ? buildVerticalWysiwygRows(args.rowData, sourceFields, columnCount, reverseColumns)
        : buildWysiwygRows(args.rowData, sourceFields, columnCount, reverseColumns)
      : [],
    columnDefs: modelEnabled
      ? layoutMode === 'vertical'
        ? buildVerticalWysiwygColumnDefs(sourceColumnDefs, columnCount)
        : buildWysiwygColumnDefs(sourceColumnDefs, columnCount)
      : [],
    allSourceFields: getColumnFields(args.columnDefs),
    reverseColumns,
  };
}

export function getWysiwygCellSource(
  rowData: RowData[],
  visualRow: WysiwygVisualRow,
  cellField: string,
  sourceFields: string[]
): WysiwygVisualCell | null {
  const visualCell = getWysiwygVisualCell(cellField, sourceFields);
  const visualColumnIndex: number = visualCell?.visualColumnIndex ?? getVerticalVisualColumnIndex(cellField);
  const sourceField: string | undefined = visualCell?.sourceField ?? visualRow.__wysiwygSourceField;
  if (!sourceField || !Number.isInteger(visualColumnIndex) || visualColumnIndex < 0) return null;

  const sourceIndex: number | null = visualRow.__sourceIndexes[visualColumnIndex];
  if (sourceIndex === null || sourceIndex < 0 || sourceIndex >= rowData.length) return null;

  return {
    sourceIndex,
    sourceField,
    visualColumnIndex,
  };
}

export function updateWysiwygCellValue(
  rowData: RowData[],
  visualRow: WysiwygVisualRow,
  cellField: string,
  sourceFields: string[],
  value: unknown
): { nextRows: RowData[]; sourceIndex: number; sourceField: string; visualColumnIndex: number } | null {
  const sourceCell: WysiwygVisualCell | null = getWysiwygCellSource(rowData, visualRow, cellField, sourceFields);
  if (!sourceCell) return null;

  const nextRows: RowData[] = [...rowData];
  const nextValue: string | number = typeof value === 'number' ? value : String(value ?? '');
  nextRows[sourceCell.sourceIndex] = {
    ...(rowData[sourceCell.sourceIndex] ?? {}),
    [sourceCell.sourceField]: nextValue,
  };

  return {
    nextRows,
    sourceIndex: sourceCell.sourceIndex,
    sourceField: sourceCell.sourceField,
    visualColumnIndex: sourceCell.visualColumnIndex,
  };
}

function getVerticalVisualColumnIndex(cellField: string): number {
  if (!cellField.startsWith('wysiwyg_vertical_')) return -1;
  const visualColumnIndex: number = Number(cellField.slice('wysiwyg_vertical_'.length));
  return Number.isInteger(visualColumnIndex) ? visualColumnIndex : -1;
}

export function createWysiwygRowsToAdd(
  columnCount: number,
  sourceFields: string[],
  existingRowCount: number = 0
): RowData[] {
  const remainder: number = existingRowCount % columnCount;
  const rowsToCompletePartialChunk: number = remainder === 0 ? 0 : columnCount - remainder;
  const rowsToAddCount: number = rowsToCompletePartialChunk + columnCount;
  return Array.from({ length: rowsToAddCount }, (): RowData => {
    const newRow: RowData = {};
    sourceFields.forEach((field: string) => {
      newRow[field] = '';
    });
    return newRow;
  });
}

export function getWysiwygIndexesFromRows(visualRows: WysiwygVisualRow[]): number[] {
  return Array.from(new Set(
    visualRows
      .flatMap((visualRow: WysiwygVisualRow) => visualRow.__sourceIndexes)
      .filter((index: number | null): index is number => typeof index === 'number')
  )).sort((a: number, b: number) => b - a);
}

export function getVerticalPairSourceKey(visualRow: WysiwygVisualRow): string {
  return visualRow.__sourceIndexes.join(',');
}

/** Keep each vertical label/value pair contiguous after managed multi-row drag. */
export function normalizeVerticalWysiwygPairOrder(
  visualRows: WysiwygVisualRow[],
  sourceFields: string[]
): WysiwygVisualRow[] {
  if (sourceFields.length < 2) return visualRows;

  const rowsByPairAndField = new Map<string, Map<string, WysiwygVisualRow>>();
  for (const visualRow of visualRows) {
    const sourceField: string | undefined = visualRow.__wysiwygSourceField;
    if (!sourceField) continue;
    const pairKey: string = getVerticalPairSourceKey(visualRow);
    const fieldRows: Map<string, WysiwygVisualRow> = rowsByPairAndField.get(pairKey) ?? new Map<string, WysiwygVisualRow>();
    if (!fieldRows.has(sourceField)) fieldRows.set(sourceField, visualRow);
    rowsByPairAndField.set(pairKey, fieldRows);
  }

  const used: Set<WysiwygVisualRow> = new Set<WysiwygVisualRow>();
  const normalizedRows: WysiwygVisualRow[] = [];
  for (const visualRow of visualRows) {
    if (visualRow.__wysiwygSourceField !== sourceFields[0] || used.has(visualRow)) continue;
    const fieldRows: Map<string, WysiwygVisualRow> | undefined = rowsByPairAndField.get(getVerticalPairSourceKey(visualRow));
    if (!fieldRows) continue;
    for (const sourceField of sourceFields) {
      const pairedRow: WysiwygVisualRow | undefined = fieldRows.get(sourceField);
      if (!pairedRow || used.has(pairedRow)) continue;
      normalizedRows.push(pairedRow);
      used.add(pairedRow);
    }
  }

  for (const visualRow of visualRows) {
    if (!used.has(visualRow)) normalizedRows.push(visualRow);
  }
  return normalizedRows;
}

/** Header drops move whole pairs, even when only one member initiated the drag. */
export function moveVerticalWysiwygPairsToTop(
  visualRows: WysiwygVisualRow[],
  draggedRows: WysiwygVisualRow[],
  sourceFields: string[]
): WysiwygVisualRow[] {
  const pairKeys = new Set(draggedRows.map(getVerticalPairSourceKey));
  const pairs = normalizeVerticalWysiwygPairOrder(visualRows, sourceFields);
  return [
    ...pairs.filter(row => pairKeys.has(getVerticalPairSourceKey(row))),
    ...pairs.filter(row => !pairKeys.has(getVerticalPairSourceKey(row))),
  ];
}

export function reorderWysiwygChunks(
  visualRows: WysiwygVisualRow[],
  rowData: RowData[]
): RowData[] {
  const reorderedRows: RowData[] = [];
  const usedIndexes: Set<number> = new Set<number>();
  for (const visualRow of visualRows) {
    const chunkSourceIndexes: number[] = visualRow.__sourceIndexes
      .filter((sourceIndex: number | null): sourceIndex is number => typeof sourceIndex === 'number')
      .sort((a: number, b: number) => a - b);
    for (const sourceIndex of chunkSourceIndexes) {
      if (typeof sourceIndex !== 'number' || usedIndexes.has(sourceIndex)) continue;
      const sourceRow: RowData | undefined = rowData[sourceIndex];
      if (sourceRow) {
        reorderedRows.push(sourceRow);
        usedIndexes.add(sourceIndex);
      }
    }
  }
  for (let index = 0; index < rowData.length; index += 1) {
    if (!usedIndexes.has(index)) reorderedRows.push(rowData[index]);
  }
  return reorderedRows;
}

export function reorderWysiwygColumns(
  rowData: RowData[],
  columnCount: number,
  visualColumnOrder: number[],
  reverseColumns: boolean
): RowData[] {
  const safeColumnCount: number = Math.max(1, Math.floor(columnCount));
  const normalizedOrder: number[] = normalizeVisualColumnOrder(visualColumnOrder, safeColumnCount);
  if (normalizedOrder.length !== safeColumnCount) return rowData;

  const reorderedRows: RowData[] = [];
  for (let chunkStart = 0; chunkStart < rowData.length; chunkStart += safeColumnCount) {
    const chunkIndex: number = Math.floor(chunkStart / safeColumnCount);
    const desiredVisualRows: RowData[] = normalizedOrder
      .map((visualColumnIndex: number): RowData | null => {
        const sourceIndex: number | null = getWysiwygSourceIndex(
          chunkIndex,
          visualColumnIndex,
          safeColumnCount,
          rowData.length,
          reverseColumns
        );
        return sourceIndex === null ? null : rowData[sourceIndex] ?? null;
      })
      .filter((row: RowData | null): row is RowData => row !== null);
    reorderedRows.push(...(reverseColumns ? [...desiredVisualRows].reverse() : desiredVisualRows));
  }
  return reorderedRows;
}

function normalizeVisualColumnOrder(visualColumnOrder: number[], columnCount: number): number[] {
  const seen: Set<number> = new Set<number>();
  const normalizedOrder: number[] = visualColumnOrder.filter((visualColumnIndex: number): boolean => {
    const isValid: boolean = Number.isInteger(visualColumnIndex) && visualColumnIndex >= 0 && visualColumnIndex < columnCount && !seen.has(visualColumnIndex);
    if (isValid) seen.add(visualColumnIndex);
    return isValid;
  });
  for (let visualColumnIndex = 0; visualColumnIndex < columnCount; visualColumnIndex += 1) {
    if (!seen.has(visualColumnIndex)) normalizedOrder.push(visualColumnIndex);
  }
  return normalizedOrder;
}

export function swapRowCellValues(
  rowData: RowData[],
  source: { sourceIndex: number; sourceField: string },
  destination: { sourceIndex: number; sourceField: string }
): RowData[] | null {
  const isInvalidCell = ({ sourceIndex, sourceField }: { sourceIndex: number; sourceField: string }): boolean =>
    sourceIndex < 0 || sourceIndex >= rowData.length || !sourceField;
  const sameRow: boolean = source.sourceIndex === destination.sourceIndex;
  if ((sameRow && source.sourceField === destination.sourceField) || isInvalidCell(source) || isInvalidCell(destination)) {
    return null;
  }

  const nextRows: RowData[] = [...rowData];
  const sourceRow: RowData = rowData[source.sourceIndex] ?? {};
  const destinationRow: RowData = rowData[destination.sourceIndex] ?? {};
  const sourceValue: string | number = sourceRow[source.sourceField] ?? '';
  const destinationValue: string | number = destinationRow[destination.sourceField] ?? '';

  if (sameRow) {
    nextRows[source.sourceIndex] = {
      ...sourceRow,
      [source.sourceField]: destinationValue,
      [destination.sourceField]: sourceValue,
    };
    return nextRows;
  }

  nextRows[source.sourceIndex] = {
    ...sourceRow,
    [source.sourceField]: destinationValue,
  };
  nextRows[destination.sourceIndex] = {
    ...destinationRow,
    [destination.sourceField]: sourceValue,
  };

  return nextRows;
}
